import { callGateway, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";
import { config } from "../config";

/**
 * An operator's organisations: the universities and companies that sponsor
 * its service on its hotspot. Published by radaccess v1.65 (resource `rosterorg`,
 * Operator + superadmin only) and served by radcustomer v0.2.32.
 *
 *   GET    /operator/{ns}/roster/organisations
 *   POST   /operator/{ns}/roster/organisations
 *   PUT    /operator/{ns}/roster/organisation/{id}
 *   PUT    /operator/{ns}/roster/organisation/{id}/plans
 *   PUT    /operator/{ns}/roster/organisation/{id}/limits
 *   GET    /operator/{ns}/roster/organisation/{id}/sponsors
 *   POST   /operator/{ns}/roster/organisation/{id}/invites
 *   DELETE /operator/{ns}/roster/organisation/{id}/invite/{inviteId}
 *   DELETE /operator/{ns}/roster/organisation/{id}/sponsor/{subject}
 *
 * 🔑 NOTHING HERE DECIDES. radcustomer checks that the organisation lives on
 * the path namespace, that every approved plan exists there, and the limits.
 *
 * Design: radius-wiki wiki/sponsor-roster-console-design.md §13.
 */

const PREFIX = "/operator";
const ns = (n: string) => encodeURIComponent(n);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SUBJECT = /^[0-9]{1,32}$/;

/**
 * The gateway rebuilds these paths from parsed ids and refuses anything else;
 * refusing here too means a bad id costs no request and never reaches a URL.
 */
function checked(kind: string, value: string, shape: RegExp): string {
  if (!shape.test(value)) {
    throw new ApiError("fail-closed", `Not a valid ${kind}: ${value}. The request was not sent.`);
  }
  return value.toLowerCase();
}
const orgPath = (n: string, orgId: string) => `${PREFIX}/${ns(n)}/roster/organisation/${checked("organisation id", orgId, UUID)}`;

export type OrganisationStatus = "active" | "suspended";

/** radcustomer model.RosterOrgLimits. */
export interface OrganisationLimits {
  max_speed_mbps: number;
  max_valid_days: number;
  /** YYYY-MM-DD. */
  latest_valid_until: string;
  updated_by?: string;
  updated_at?: string;
}

/** radcustomer model.RosterOrgSummary. */
export interface Organisation {
  organisation_id: string;
  namespace: string;
  name: string;
  status: OrganisationStatus;
  member_ref_label: string;
  group_label_label: string;
  location_label_label: string;
  approved_plans: string[] | null;
  limits?: OrganisationLimits;
  sponsor_count: number;
  pending_invite_count: number;
  members: { total: number; pending: number; activated: number; revoked: number };
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at?: string;
}

/** The fields an operator edits. Labels name the three optional roster columns. */
export interface OrganisationFields {
  name: string;
  member_ref_label: string;
  group_label_label: string;
  location_label_label: string;
}

/** radcustomer model.RosterSponsorLink. */
export interface SponsorLink {
  subject: string;
  created_by: string;
  created_at: string;
  /** Copied from the invite at claim; unverified. */
  email?: string;
  /** Whether the weekly counts-only summary goes to `email`. */
  digest?: boolean;
}

/** The two ways an invitation is sent (radcomm, design §13.8). */
export type InviteChannel = "email" | "sms";

/** radcustomer model.RosterNoticeState: never the message itself. */
export interface InviteDelivery {
  status: "queued" | "sending" | "sent" | "failed" | "waiting_dlt" | "expired";
  attempts: number;
  sent_at?: string;
}

/** radcustomer model.RosterInviteRow. */
export interface Invite {
  invite_id: string;
  mobile: string;
  full_name: string;
  status: "pending" | "claimed" | "cancelled" | "expired";
  expires_at: string;
  invited_by: string;
  invited_at: string;
  claimed_subject?: string;
  claimed_at?: string;
  cancelled_at?: string;
  /** Optional, unverified; the invitation was emailed here. */
  email?: string;
  /** Per channel; absent for an invite made without delivery. */
  delivery?: Partial<Record<InviteChannel, InviteDelivery>>;
  /**
   * A pending invite whose link no longer works (radcustomer v0.2.39+, which
   * decides it on its own clock). Absent from an older radcustomer, and then
   * the invite reads as before.
   */
  expired?: boolean;
}

/** A new invite. `token` is shown ONCE: radcustomer keeps only its hash. */
export interface NewInvite {
  invite_id: string;
  mobile: string;
  full_name: string;
  token: string;
  expires_at: string;
  email?: string;
  /** What was queued, per channel ("queued"); empty when nothing is sent. */
  delivery?: Partial<Record<InviteChannel, string>>;
}

export const listOrganisations = (i: CallerIdentity, n: string) =>
  callGateway<{ namespace: string; organisations: Organisation[] | null }>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/organisations`);

export const createOrganisation = (i: CallerIdentity, n: string, f: OrganisationFields) =>
  callGateway<{ organisation_id: string; name: string; status: OrganisationStatus }>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/roster/organisations`, {
    method: "POST",
    body: f,
  });

/**
 * 🚨 THE WHOLE RECORD, EVERY TIME. radcustomer's update replaces all five
 * fields and reads a missing status as `active` -- so a rename sent without the
 * status would quietly re-activate a suspended organisation. Status is
 * therefore a required argument, not an optional field.
 */
export const updateOrganisation = (i: CallerIdentity, n: string, orgId: string, f: OrganisationFields, status: OrganisationStatus) =>
  callGateway<{ organisation_id: string; name: string; status: OrganisationStatus }>(i, config().gatewayBase, orgPath(n, orgId), {
    method: "PUT",
    body: { ...f, status },
  });

/** Replaces the approved list. Every plan must exist on the namespace. */
export const setApprovedPlans = (i: CallerIdentity, n: string, orgId: string, plans: string[]) =>
  callGateway<{ organisation_id: string; plans: string[] }>(i, config().gatewayBase, `${orgPath(n, orgId)}/plans`, {
    method: "PUT",
    body: { plans },
  });

export const setLimits = (i: CallerIdentity, n: string, orgId: string, l: Pick<OrganisationLimits, "max_speed_mbps" | "max_valid_days" | "latest_valid_until">) =>
  callGateway<{ organisation_id: string; limits: OrganisationLimits }>(i, config().gatewayBase, `${orgPath(n, orgId)}/limits`, {
    method: "PUT",
    body: l,
  });

export const listSponsors = (i: CallerIdentity, n: string, orgId: string) =>
  callGateway<{ organisation_id: string; sponsors: SponsorLink[] | null; invites: Invite[] | null }>(i, config().gatewayBase, `${orgPath(n, orgId)}/sponsors`);

/**
 * Creates the invite and asks for it to be SENT (§13.8): by email when one is
 * given, by SMS once the DLT template exists. `linkBase` is this console's
 * origin; radcustomer only accepts an AnkhOS console, because the link leaves
 * in an AnkhOS email.
 */
export const createInvite = (i: CallerIdentity, n: string, orgId: string, mobile: string, fullName: string, email: string, linkBase: string) =>
  callGateway<NewInvite>(i, config().gatewayBase, `${orgPath(n, orgId)}/invites`, {
    method: "POST",
    body: { mobile, full_name: fullName, ...(email.trim() ? { email: email.trim() } : {}), link_base: linkBase },
  });

/** The server's check (org_sponsor_invite.c_invite_email), so a typo is caught before a request. */
export function isInviteEmail(raw: string): boolean {
  const e = raw.trim();
  return e.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
}

/** What the Sponsors card shows for a pending invite. */
export interface InviteState {
  label: "invited" | "expired";
  color: "warning" | "default";
  when: string;
  /** A short instruction, only when the operator has something to do. */
  hint?: string;
}

/**
 * An expired invite stays pending until it is cancelled or replaced, and was
 * shown as "invited" with a past date (owner, 2026-10-08). Inviting the same
 * mobile again now replaces it, so the card says so.
 */
export function inviteState(i: Pick<Invite, "expired" | "expires_at">): InviteState {
  if (i.expired) {
    return {
      label: "expired", color: "default", when: `expired ${i.expires_at}`,
      hint: "The link no longer works. Invite the same mobile again to replace it.",
    };
  }
  return { label: "invited", color: "warning", when: `expires ${i.expires_at}` };
}

/** One line per channel for the Sponsors card. */
export function deliveryLabel(channel: InviteChannel, d: InviteDelivery | undefined): string | null {
  if (!d) return null;
  if (channel === "email") {
    switch (d.status) {
      case "queued":
        return d.attempts > 0 ? `email retrying (attempt ${d.attempts} failed)` : "email queued";
      case "sending":
        return "emailing now";
      case "sent":
        return d.sent_at ? `emailed ${d.sent_at}` : "emailed";
      case "failed":
        return "email failed: copy the link yourself (cancel and invite again)";
      case "expired":
        return "email not sent: the invite ended first";
      default:
        return null;
    }
  }
  switch (d.status) {
    case "queued":
    case "waiting_dlt":
      return "SMS waits for the SMS template's approval";
    case "sending":
      return "sending SMS now";
    case "sent":
      return d.sent_at ? `SMS sent ${d.sent_at}` : "SMS sent";
    case "failed":
      return "SMS failed";
    case "expired":
      return "SMS not sent: the invite ended first";
    default:
      return null;
  }
}

export const cancelInvite = (i: CallerIdentity, n: string, orgId: string, inviteId: string) =>
  callGateway<{ invite_id: string; status: "cancelled" }>(i, config().gatewayBase, `${orgPath(n, orgId)}/invite/${checked("invite id", inviteId, UUID)}`, {
    method: "DELETE",
  });

/** The login stays; it reaches this organisation no more. */
export const unlinkSponsor = (i: CallerIdentity, n: string, orgId: string, subject: string) =>
  callGateway<{ organisation_id: string; subject: string; unlinked: boolean; grant_revoked: boolean }>(
    i,
    config().gatewayBase,
    `${orgPath(n, orgId)}/sponsor/${checked("sponsor login", subject, SUBJECT)}`,
    { method: "DELETE" },
  );

/**
 * The link the operator sends. The token rides in the query and is read by the
 * public claim page; this console never stores it.
 */
export function inviteLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/sponsor/claim?invite=${encodeURIComponent(token)}`;
}
